import type { PlacementStart, SuccessorLine, TimelineEntry } from '../api/types.ts';
import { visitTypeLabel } from '../visitTypes.ts';
import { formatDate } from '../dates.ts';
import { useState } from 'react';
import { VisitDetails } from './VisitDetails.tsx';

function fmt(date: string | null): string {
  if (!date) return '—';
  return formatDate(date);
}

/**
 * The end of a period. "present" only for what is current: a placement that
 * has ended with no recorded end date (the load's undated rows, ended by a
 * correction) is not still running, and must not read as if it were.
 */
function until(e: TimelineEntry): string {
  if (e.ended_on) return formatDate(e.ended_on);
  return e.is_current ? 'present' : 'end not recorded';
}

/** The start of a period: a missing date says so, never a bare dash. */
function from(e: TimelineEntry): string {
  return e.started_on ? formatDate(e.started_on) : 'start not recorded';
}

function duration(days: number | null): string {
  if (days === null) return '';
  if (days < 60) return `${days} days`;
  const months = Math.round(days / 30.4);
  if (months < 24) return `${months} months`;
  return `${(days / 365.25).toFixed(1)} years`;
}

/**
 * Where this client has been, newest first.
 *
 * Placements and temporary stays in one sequence, because that is how someone
 * reads a history: lived at Ozanam Hall from March, at Kings County Hospital
 * for eleven days in June with the bed held, back at Ozanam Hall since. Two
 * panels would make the reader interleave the dates themselves, which is the
 * work this screen exists to do for them.
 *
 * **A stay is an interruption, not a move**, and it has to look like one. It is
 * inset, hairline-ruled, and says where they still lived — the placement did
 * not end, the bed was held, the mail still went to the facility. Flattening
 * the two into identical rows would say the client moved to hospital and then
 * moved back, which is the reading the schema keeps three tables apart to
 * prevent.
 *
 * Visits appear here too, as events rather than periods — a visit that is
 * recorded and then shows nowhere reads as a failed save on exactly the
 * connection this was built for.
 *
 * Resolved stays matter most here. An open one already shows in the header;
 * a five-day admission that ended is the thing seven years of spreadsheets
 * could not record, and until this view existed the system recorded it and
 * then showed it nowhere.
 */
export function PlacementTimeline({
  entries,
  successor = null,
  placementStart = null,
  endReasons = [],
}: {
  entries: TimelineEntry[];
  successor?: SuccessorLine | null;
  placementStart?: PlacementStart | null;
  /** How placements ended, where the ending has a name (093): "Left without notice". */
  endReasons?: { residence_id: string; end_reason: string }[];
}) {
  // One visit open at a time, inline under its row (125): its details, and Edit and Withdraw for those who may.
  const [openVisit, setOpenVisit] = useState<string | null>(null);
  const endReasonOf = (e: TimelineEntry) =>
    e.entry_kind === 'Placement' ? endReasons.find((r) => r.residence_id === e.entry_id)?.end_reason ?? null : null;
  // A period that has ended with no recorded end has no knowable length: the
  // view counts it to today, which would read as months the client was there.
  const knownDays = (e: TimelineEntry) => (!e.is_current && !e.ended_on ? null : e.days);
  // An asserted start (092) is an effective date, not a known one: its row says so.
  const assertedStart = (e: TimelineEntry) =>
    e.entry_kind === 'Placement' && !!placementStart?.started_on_unknown && e.entry_id === placementStart.residence_id;
  if (entries.length === 0 && !successor) {
    return (
      <p className="px-4 py-8 text-center text-neutral-500">
        No placement history recorded.
      </p>
    );
  }

  // THE SUCCESSOR LINE (Arthur, 2 October). Above it, the agency's
  // responsibility; below it, the previous guardian's period, kept and shown
  // and plainly not ours. Nothing is hidden. BELOW is what was wholly over before
  // the agency was appointed: a visit before the date, or a placement or stay
  // that had ended by it. A placement that began before and was still running
  // SPANS the line and stays above, marked as begun before (0518: living at her
  // home since before the appointment, and still there; under "the first entry
  // that started before" her current home read as the previous guardian's). An
  // undated entry stays above, since nothing says it was over. A date not
  // recorded puts the line at the top, saying so, rather than guessing.
  const since = successor?.since ?? null;
  const startedBefore = (e: TimelineEntry) => since !== null && e.started_on !== null && e.started_on < since;
  const whollyBefore = (e: TimelineEntry) =>
    startedBefore(e) && (e.entry_kind === 'Visit' || (e.ended_on !== null && e.ended_on <= since!));
  const ordered = since !== null
    ? [...entries.filter((e) => !whollyBefore(e)), ...entries.filter(whollyBefore)]
    : entries;
  const beforeOnPage = since !== null ? entries.filter(whollyBefore).length : 0;
  const lineAt = !successor ? -1 : since === null ? 0 : ordered.length - beforeOnPage;
  const olderOffPage = successor && since !== null && beforeOnPage === 0 && successor.before > 0 ? successor.before : 0;
  const nothingBefore = !!successor && since !== null && beforeOnPage === 0 && successor.before === 0;

  const rows = ordered.map((e) =>
        e.entry_kind === 'Visit' ? (
          /*
            An event, not a period. One date, no range, no duration in days —
            a rule down the left edge rather than a block, so the eye reads the
            placements as the structure and the visits as marks against it.

            `lives_at` is set only when the visit happened somewhere other than
            where the client lived, so the line below only appears when there
            is something to say. "Visited at Crestwood Green · they live at
            Pembroke Point" is the sentence a court report needs; at their own
            placement there is nothing to add and saying it would be noise.
          */
          <li key={e.entry_id} className="py-3 pr-4 pl-8">
            <div className="border-l-2 border-neutral-900/25 pl-3">
              <div className="flex items-baseline gap-2">
                <p className="text-xs font-bold tracking-[0.12em] text-neutral-600 uppercase">
                  {visitTypeLabel(e.kind_label)}
                  {e.withdrawn && <span className="ml-2 rounded bg-neutral-200 px-1.5 py-0.5 text-neutral-800">Withdrawn</span>}
                </p>
                <p className="ml-auto shrink-0 text-sm text-neutral-500 tabular-nums">
                  {fmt(e.started_on)}
                  {e.minutes !== null && ` · ${e.minutes} min`}
                </p>
              </div>

              <p className="mt-0.5 text-[0.9375rem] text-neutral-800">
                {e.location ? (
                  <>Visited at <span className="font-semibold">{e.location}</span></>
                ) : (
                  'Visited'
                )}
                {e.lives_at && (
                  <span className="text-neutral-600"> · they live at {e.lives_at}</span>
                )}
              </p>

              {e.outcome && (
                <p className="mt-1 inline-block rounded bg-warning-bg px-2 py-0.5 text-sm
                              font-semibold text-warning-fg">
                  {e.outcome}
                </p>
              )}

              {/* The note's preview, the current text; not while the details below show it in full (19 October). */}
              {e.reason && openVisit !== e.entry_id && (
                <p className="mt-1 line-clamp-3 text-sm leading-snug text-neutral-600">
                  {e.reason}
                </p>
              )}

              {e.recorded_by && (
                <p className="mt-1 text-xs text-neutral-500">{e.recorded_by}</p>
              )}

              {e.withdrawn && (
                <p className="mt-1 text-sm text-neutral-700">
                  Withdrawn: {e.withdrawal_reason}. Kept on record; it counts toward nothing.
                </p>
              )}

              {openVisit === e.entry_id ? (
                <VisitDetails id={e.entry_id} onClose={() => setOpenVisit(null)} />
              ) : (
                <button type="button" onClick={() => setOpenVisit(e.entry_id)}
                        className="mt-1 min-h-[44px] text-sm font-semibold text-neutral-700 underline underline-offset-2">
                  Details
                </button>
              )}
            </div>
          </li>
        ) : e.entry_kind === 'Stay' ? (
          <li key={e.entry_id} className="bg-neutral-50 py-3 pr-4 pl-8">
            <div className="border-l-2 border-neutral-300 pl-3">
              <p className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">
                Away · {e.kind_label}
                {e.is_current && ' · now'}
              </p>

              <p className="mt-0.5 font-semibold text-neutral-900">
                {e.location ?? 'Location not recorded'}
              </p>

              <p className="mt-1 text-sm text-neutral-600 tabular-nums">
                {from(e)} – {until(e)}
                {knownDays(e) !== null && <> · {duration(knownDays(e)!)}</>}
                {startedBefore(e) && !whollyBefore(e) && <> · began before Allied was appointed</>}
              </p>

              {/* The placement did not end. Say so on the row itself, so the
                  reader never has to scan for what they went back to. */}
              {e.lives_at && (
                <p className="mt-1 text-sm text-neutral-600">
                  {e.bed_held ? 'Bed held at' : 'Lived at'}{' '}
                  <span className="font-medium text-neutral-800">{e.lives_at}</span>
                </p>
              )}

              {e.reason && (
                <p className="mt-1 text-sm text-neutral-600">Reason: {e.reason}</p>
              )}

              {e.outcome && e.outcome !== 'Returned' && (
                <p className="mt-1 text-sm font-medium text-neutral-700">{e.outcome}</p>
              )}
            </div>
          </li>
        ) : (
          <li key={e.entry_id} className="px-4 py-3.5">
            <div className="flex items-baseline gap-2">
              <p className="min-w-0 font-semibold text-neutral-900">
                {e.location ?? 'Location not recorded'}
              </p>
              {e.is_current && (
                <span className="ml-auto shrink-0 rounded-full bg-neutral-900 px-2.5 py-0.5
                                 text-xs font-bold tracking-wide text-white uppercase">
                  Current
                </span>
              )}
            </div>

            <p className="mt-0.5 text-[0.9375rem] text-neutral-600">
              {[e.kind_label, e.city].filter(Boolean).join(' · ')}
            </p>

            <p className="mt-1 text-sm text-neutral-500 tabular-nums">
              {assertedStart(e) ? (
                /* An effective date, not a known one (092): no date and no
                   duration counted from it. */
                <>Since before Allied's involvement, date not known – {until(e)}{endReasonOf(e) && <> · {endReasonOf(e)!.toLowerCase()}</>}</>
              ) : (
                <>
                  {from(e)} – {until(e)}
                  {knownDays(e) !== null && <> · {duration(knownDays(e)!)}</>}
                  {startedBefore(e) && !whollyBefore(e) && <> · began before Allied was appointed</>}
                  {endReasonOf(e) && <> · {endReasonOf(e)!.toLowerCase()}</>}
                </>
              )}
            </p>

            {e.reason && (
              <p className="mt-1 text-sm text-neutral-600">Reason: {e.reason}</p>
            )}
          </li>
        ),
  );

  if (successor) {
    rows.splice(
      lineAt,
      0,
      <SuccessorLineRow
        key="successor-line"
        since={since}
        nothingBefore={nothingBefore}
        olderOffPage={olderOffPage}
      />,
    );
  }

  return <ol className="divide-y divide-neutral-200">{rows}</ol>;
}

function SuccessorLineRow({
  since,
  nothingBefore,
  olderOffPage,
}: {
  since: string | null;
  nothingBefore: boolean;
  olderOffPage: number;
}) {
  return (
    <li className="border-y-2 border-tenure-line bg-tenure-bg px-4 py-3" aria-label="Allied appointed as successor guardian">
      <p className="text-xs font-bold tracking-[0.12em] text-tenure-fg uppercase">
        {since ? <>Allied appointed as successor · {fmt(since)}</> : 'Allied appointed as successor · date not recorded'}
      </p>
      <p className="mt-1 text-sm text-tenure-fg">
        {!since
          ? <>The date Allied was appointed is not on record, so this history is not divided. Record the appointing order's date to place the line.</>
          : nothingBefore
            ? <>Above this line: Allied's responsibility. Nothing is recorded from before Allied's appointment: the previous guardian's records are not held here.</>
            : <>Above this line: Allied's responsibility. Below it: before Allied was appointed, the previous guardian's period, kept as recorded.</>}
      </p>
      {olderOffPage > 0 && (
        <p className="mt-1 text-sm text-tenure-fg">
          {olderOffPage} earlier {olderOffPage === 1 ? 'entry' : 'entries'} from before Allied's appointment {olderOffPage === 1 ? 'is' : 'are'} beyond this page.
        </p>
      )}
    </li>
  );
}
